import React, { useState, useEffect } from 'react';
import './MulliganModal.css';
import CardDetail from './CardDetail.js';
import { createParticleExplosion } from '../utils/animationUtils.js';
import { NPC_PLAYER_ID } from '../gameLogic/constants.js';
import { getCardName, getUILoc, getCardTypeName } from '../utils/cardUtils.js';
import { CardIllustration } from './Card.js';
import audioManager from '../utils/AudioManager.js';

const MulliganModal = ({ hand, onConfirmMulligan, cardDefs, mulliganState, firstPlayer }) => {
    const [selectedCardIds, setSelectedCardIds] = useState([]);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [selectedDetailCard, setSelectedDetailCard] = useState(null);

    const isFirstPlayer = firstPlayer === 'PLAYER1';
    const turnOrderLabel = isFirstPlayer ? getUILoc('MESSAGES.MULLIGAN_FIRST_PLAYER') : getUILoc('MESSAGES.MULLIGAN_SECOND_PLAYER');

    useEffect(() => {
        if (isConfirmed) {
            // Use setTimeout to allow React to render the new hand before we search for the elements
            const timer = setTimeout(() => {
                hand.forEach(card => {
                    if (card.isNew) {
                        const cardElement = document.querySelector(`.mulligan-card[data-card-id="${card.instance_id}"]`);
                        if (cardElement) {
                            createParticleExplosion(cardElement, card.card_type);
                        }
                    }
                });
            }, 50); 
            return () => clearTimeout(timer);
        }
    }, [isConfirmed, hand]);

    const handleCardClick = (cardId) => {
        if (isConfirmed) return;
        
        setSelectedCardIds(prevSelected => {
            if (prevSelected.includes(cardId)) {
                audioManager.playSE('cancel');
                return prevSelected.filter(id => id !== cardId);
            } else {
                audioManager.playSE('select');
                return [...prevSelected, cardId];
            }
        });
    };

    const handleDetailClick = (e, card) => {
        e.stopPropagation(); // 親のonClick(マリガン選択)を阻止
        audioManager.playSE('select');
        setSelectedDetailCard(card);
    };

    const handleConfirm = () => {
        if (isConfirmed) return;
        audioManager.playSE('select');
        setIsConfirmed(true);
        onConfirmMulligan(selectedCardIds);
    };

    const npcCulliganedCount = mulliganState[NPC_PLAYER_ID]?.count || 0;

    return (
        <div className="mulligan-overlay">
            <div className="mulligan-modal">
                <div className="mulligan-header-row">
                    <h2>{isConfirmed ? getUILoc('STATUS.NEW_HAND') : getUILoc('STATUS.MULLIGAN')}</h2>
                    <span className={`mulligan-turn-order-badge ${isFirstPlayer ? 'first-turn' : 'second-turn'}`}>
                        {turnOrderLabel}
                    </span>
                </div>
                <p>{isConfirmed ? getUILoc('MESSAGES.MULLIGAN_DONE') : getUILoc('MESSAGES.MULLIGAN_PROMPT')}</p>
                {isConfirmed && (
                    <p className="npc-mulligan-info">
                        {getUILoc('MESSAGES.NPC_MULLIGAN_INFO', { 
                            player: getUILoc('PLAYER_NAME.OPPONENT'), 
                            count: npcCulliganedCount 
                        })}
                    </p>
                )}
                <div className="mulligan-hand-container">
                    {hand.map(card => {
                        const cardDef = cardDefs[card.id];
                        const isSelected = !isConfirmed && selectedCardIds.includes(card.instance_id);
                        return (
                            <div
                                key={card.instance_id}
                                className={`mulligan-card card-game ${isSelected ? 'selected' : ''}`}
                                data-card-id={card.instance_id}
                                onClick={() => handleCardClick(card.instance_id)}
                            >
                                <CardIllustration cardId={card.id} isBackground={true} />
                                <div className="mulligan-card-name">{getCardName(card.id)}</div>
                                <div className="mulligan-card-cost">{getUILoc('LABELS.REQUIRED_SCALE')}: {card.required_scale}</div>
                                <div className="mulligan-card-type">{getCardTypeName(card.card_type)}</div>
                                
                                {!isConfirmed && (
                                    <button 
                                        className="mulligan-card-detail-button"
                                        onClick={(e) => handleDetailClick(e, cardDef)}
                                    >
                                        {getUILoc('BUTTONS.DETAIL')}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
                {isConfirmed && (
                    <div className="mulligan-progress-bar-container">
                        <div className="mulligan-progress-bar"></div>
                    </div>
                )}
                {!isConfirmed && (
                    <div className="mulligan-actions">
                        <button onClick={handleConfirm}>
                            {getUILoc('BUTTONS.CONFIRM')} ({getUILoc('UNITS.CARDS', { count: selectedCardIds.length })})
                        </button>
                    </div>
                )}
            </div>

            {/* 詳細モーダル */}
            {selectedDetailCard && (
                <CardDetail 
                    card={selectedDetailCard}
                    cardDefs={cardDefs}
                    onClose={() => {
                        audioManager.playSE('cancel');
                        setSelectedDetailCard(null);
                    }}
                />
            )}
        </div>
    );
};

export default MulliganModal;
